import { useEffect, useState } from "react";
import {
  Button,
  Dropdown,
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "semantic-ui-react";
import { useMeetingNotesTemplates } from "../../hooks/useMeetingNotesTemplates";
import { style } from "../../style";

// Sentinel for the built-in prompt. It has to be a non-empty string: Semantic
// UI's Dropdown treats "" as "no value at all" and renders the placeholder
// instead of the selected option.
const BUILT_IN = "__built_in__";

// A small modal for picking which meeting notes template to summarise with,
// pre-selected to the user's own resolved default. Only mounted by callers
// while it's open — with no custom templates, there's nothing to choose and
// callers summarise directly instead.
//
// NOTE: picking BUILT_IN means "use the built-in prompt", an explicit
// override that bypasses the user/customer default chain entirely, and it
// goes to the API as null. That is not the same as the "" (cleared) value in
// UserForm's template dropdown, which means "no personal override, inherit
// the customer default" and may itself still resolve to the built-in.
export function SummariseTemplatePicker({ open, onClose, onConfirm }) {
  const { templates, meta, isLoading: templatesLoading } =
    useMeetingNotesTemplates();
  const [templateId, setTemplateId] = useState(BUILT_IN);

  useEffect(() => {
    if (open) {
      setTemplateId(meta.user_default_id ?? BUILT_IN);
    }
  }, [open, meta.user_default_id]);

  const options = [
    {
      key: BUILT_IN,
      value: BUILT_IN,
      text: meta.built_in?.name ?? "Sidekick default",
    },
    ...templates.map((template) => ({
      key: template.id,
      value: template.id,
      text: template.name,
    })),
  ];

  return (
    <Modal size="tiny" open={open} onClose={onClose}>
      <ModalHeader>Choose a meeting notes template</ModalHeader>
      <ModalContent>
        <p>Which template should be used to summarise this call?</p>
        <Dropdown
          search
          selection
          fluid
          loading={templatesLoading}
          disabled={templatesLoading}
          options={options}
          value={templateId}
          onChange={(_, { value }) => setTemplateId(value)}
        />
      </ModalContent>
      <ModalActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          style={style.gradientButton}
          loading={templatesLoading}
          disabled={templatesLoading}
          onClick={() =>
            onConfirm(templateId === BUILT_IN ? null : templateId)
          }
        >
          Summarise
        </Button>
      </ModalActions>
    </Modal>
  );
}
